import { useEffect, useState } from "react";

export type RouteMatch =
  | { kind: "agent-hub" }
  | { kind: "agent-commissions" }
  | { kind: "agent-payouts" }
  | { kind: "agent-links" }
  | { kind: "agent-team" }
  | { kind: "agent-upgrade" }
  | { kind: "agent-stats" }
  | { kind: "agent-account" }
  | { kind: "agent-page-editor" }
  | { kind: "agent-help" }
  | { kind: "agent-vorlagen" }
  | { kind: "public-join" }
  | { kind: "public-agb" }
  | { kind: "public-datenschutz" }
  | { kind: "public-landing-page"; agentCode: string }
  | { kind: "admin-hub" }
  | { kind: "admin-agents-list" }
  | { kind: "admin-agents-new" }
  | { kind: "admin-agent-detail"; agentId: string }
  | { kind: "admin-commissions" }
  | { kind: "admin-payouts" }
  | { kind: "admin-kyc" }
  | { kind: "admin-commission-rules" }
  | { kind: "admin-settings" }
  | { kind: "admin-audit-log" }
  | { kind: "admin-activity" }
  | { kind: "admin-catalog-blocks" }
  | { kind: "admin-webhook-health" }
  | { kind: "admin-attributions" }
  | { kind: "admin-shop-sync" }
  | { kind: "admin-ideen" }
  | { kind: "admin-errors" }
  | { kind: "admin-customer-orders" }
  | { kind: "admin-customer-order-detail"; orderId: string }
  | { kind: "agent-customers" }
  | { kind: "agent-customer-directory" }
  | { kind: "agent-rechnungscheck" }
  | { kind: "agent-prospekte" }
  | { kind: "not-found" };

export function matchRoute(path: string): RouteMatch {
  if (path === "/" || path === "") return { kind: "agent-hub" };
  if (path === "/commissions" || path === "/commissions/")
    return { kind: "agent-commissions" };
  if (path === "/payouts" || path === "/payouts/") return { kind: "agent-payouts" };
  if (path === "/links" || path === "/links/") return { kind: "agent-links" };
  if (path === "/team" || path === "/team/") return { kind: "agent-team" };
  if (path === "/customers" || path === "/customers/" || path === "/kunden" || path === "/kunden/")
    return { kind: "agent-customers" };
  if (path === "/kundenliste" || path === "/kundenliste/")
    return { kind: "agent-customer-directory" };
  if (path === "/rechnungscheck" || path === "/rechnungscheck/")
    return { kind: "agent-rechnungscheck" };
  if (path === "/prospekte" || path === "/prospekte/" || path === "/prospekt" || path === "/prospekt/")
    return { kind: "agent-prospekte" };
  if (path === "/upgrade" || path === "/upgrade/") return { kind: "agent-upgrade" };
  if (path === "/stats" || path === "/stats/") return { kind: "agent-stats" };
  if (path === "/account" || path === "/account/") return { kind: "agent-account" };
  if (path === "/page-editor" || path === "/page-editor/") return { kind: "agent-page-editor" };
  if (path === "/hilfe" || path === "/hilfe/" || path === "/help" || path === "/help/")
    return { kind: "agent-help" };
  if (path === "/vorlagen" || path === "/vorlagen/")
    return { kind: "agent-vorlagen" };
  if (path === "/join" || path.startsWith("/join/")) return { kind: "public-join" };
  if (path === "/agb" || path === "/agb/") return { kind: "public-agb" };
  if (path === "/datenschutz" || path === "/datenschutz/") return { kind: "public-datenschutz" };

  if (path === "/admin" || path === "/admin/") return { kind: "admin-hub" };
  if (path === "/admin/agents" || path === "/admin/agents/")
    return { kind: "admin-agents-list" };
  if (path === "/admin/agents/new") return { kind: "admin-agents-new" };
  if (path === "/admin/commissions" || path === "/admin/commissions/")
    return { kind: "admin-commissions" };
  if (path === "/admin/payouts" || path === "/admin/payouts/")
    return { kind: "admin-payouts" };
  if (path === "/admin/kyc" || path === "/admin/kyc/") return { kind: "admin-kyc" };
  if (path === "/admin/commission-rules" || path === "/admin/commission-rules/")
    return { kind: "admin-commission-rules" };
  if (path === "/admin/settings" || path === "/admin/settings/")
    return { kind: "admin-settings" };
  if (path === "/admin/audit-log" || path === "/admin/audit-log/")
    return { kind: "admin-audit-log" };
  if (path === "/admin/activity" || path === "/admin/activity/")
    return { kind: "admin-activity" };
  if (path === "/admin/catalog-blocks" || path === "/admin/catalog-blocks/")
    return { kind: "admin-catalog-blocks" };
  if (path === "/admin/webhook-health" || path === "/admin/webhook-health/")
    return { kind: "admin-webhook-health" };
  if (path === "/admin/attributions" || path === "/admin/attributions/")
    return { kind: "admin-attributions" };
  if (path === "/admin/shop-sync" || path === "/admin/shop-sync/")
    return { kind: "admin-shop-sync" };
  if (path === "/admin/ideen" || path === "/admin/ideen/")
    return { kind: "admin-ideen" };
  if (path === "/admin/errors" || path === "/admin/errors/")
    return { kind: "admin-errors" };

  if (path === "/admin/customer-orders" || path === "/admin/customer-orders/")
    return { kind: "admin-customer-orders" };

  const coDetail = path.match(/^\/admin\/customer-orders\/([0-9a-f-]{36})\/?$/);
  if (coDetail) return { kind: "admin-customer-order-detail", orderId: coDetail[1] };

  const detail = path.match(/^\/admin\/agents\/([0-9a-f-]{36})\/?$/);
  if (detail) return { kind: "admin-agent-detail", agentId: detail[1] };

  const publicPage = path.match(/^\/p\/([^/]+)\/?$/);
  if (publicPage) return { kind: "public-landing-page", agentCode: decodeURIComponent(publicPage[1]) };

  return { kind: "not-found" };
}

export function navigate(path: string): void {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function usePath(): string {
  const [path, setPath] = useState<string>(() => window.location.pathname || "/");
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname || "/");
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return path;
}
